import Component from '../core/Component'
import throttle from 'lodash/throttle'
import { queryAll, toggleClass } from '../utils/dom'
import support from '../utils/BrowserSupport'
import { enableZoom, disableZoom } from '../services/Viewport/zoom'

export const STATES = {
    DRAGGING: 'is-dragging',
    PINCHING: 'is-pinching',
    READY: 'is-ready',
    ACTIVE: 'is-active',
    HIGHLIGHTED: 'is-highlighted',
    FILTERED: 'is-filtered',
}

export const defaults = {
    default: null,
}

const ZOOM_MIN = 0.3
const ZOOM_MAX = 4
const ZOOM_STEPS = 16
const ZOOM_DEFAULT = 1
const ZOOM_STEP_FACTOR = 1.2
const AREA_LOGO_RATIO = 0.8
const LOGO_VISIBILITY_THRESHOLD = 30
const LOGO_BASE_WIDTH = 100
const BUILDING_CONTAINER_RATIO = 0.9
const TOOLBAR_SIZE = 35 + 70
const LIMIT_BORDER_RATIO = 0.3
const SVG_PADDING_X = 115
const SVG_PADDING_Y = 200

export default class PlanCanvas extends Component {
    constructor(element) {
        super(element, defaults)

        this.ref = {
            canvas: null,
            container: null,
            center: null,
            svg: null,
            scroll: null,
            stores: [],
            logos: [],
            floors: [],
            buildings: [],
            symbols: [],
        }

        const [x, y, width, height] = this.ref.svg
            .getAttribute('viewBox')
            .split(' ')
            .map((value) => parseInt(value))

        this.viewBox = {
            x,
            y,
            width,
            height,
        }

        this.state = {
            clientXStart: 0,
            clientYStart: 0,
            dragging: false,
            pinching: false,
            pinchDistanceStart: 1,
            pinchFactorStart: 1,
            building: 'main',
            clickActive: true,
        }

        this.matrix = {
            width: width,
            height: height,
            x: 0,
            y: 0,
            startX: 0,
            startY: 0,
            scale: this.getZoomFactor(ZOOM_DEFAULT),
            scaleStep: ZOOM_DEFAULT,
        }

        this.currentFloor = null
        this.currentStore = null
    }

    prepare() {
        this.ref.svg.setAttribute('width', this.matrix.width)
        this.ref.svg.setAttribute('height', this.matrix.height)
        this.ref.scroll.style.width = `${this.matrix.width}px`
        this.ref.scroll.style.height = `${this.matrix.height}px`
        this.ref.scroll.addEventListener(support.transitionEnd, this.handleScrollTransitionEnd)

        this.center()

        this.element.addEventListener('mousedown', this.handlePanStart)
        this.element.addEventListener('mousemove', this.handlePan)
        this.element.addEventListener('mouseup', this.handlePanEnd)
        this.element.addEventListener('mouseleave', this.handlePanEnd)
        this.element.addEventListener('click', this.handleStoreClick)

        this.element.addEventListener('touchstart', this.handleTouchStart)
        this.element.addEventListener('touchmove', this.handleTouchMove)
        this.element.addEventListener('touchend', this.handleTouchEnd)
        this.element.addEventListener('touchcancel', this.handleTouchEnd)

        this.ref.stores.forEach((element) => {
            element.addEventListener('dragstart', (event) => event.preventDefault())
            // element.addEventListener('click', this.handleStoreClick)
            element.addEventListener('mouseenter', this.handleStoreMouseEnter)
            element.addEventListener('mouseleave', this.handleStoreMouseLeave)
        })
        this.ref.floors.forEach((element) =>
            element.addEventListener(support.transitionEnd, () => {
                if (!element.classList.contains('is-active')) {
                    element.classList.remove('is-visible')
                }
            })
        )

        this.ref.symbols.forEach((element) => {
            element.addEventListener('dragstart', (event) => event.preventDefault())
            element.addEventListener('mouseenter', this.handleStoreMouseEnter)
            element.addEventListener('mouseleave', this.handleStoreMouseLeave)
        })

        this.parse()
        if (this.options.default !== null) {
            this.setFloor(this.options.default)
        }
        this.renderStores()

        setTimeout(() => this.element.classList.add(STATES.READY))
    }

    destroy() {
        window.removeEventListener('resize', this.handleResize)
        enableZoom()
    }

    handleScrollTransitionEnd = (event) => {
        if (event.target !== this.ref.scroll) {
            return
        }
        this.emit('canvas:transition:end')
    }

    handleTouchStart = (event) => {
        if (event.touches.length === 1) {
            this.handlePanStart(event)
        } else if (this.state.dragging) {
            this.handlePanEnd()
        }

        if (event.touches.length === 2) {
            this.handlePinchStart(event)
        } else if (this.state.pinching) {
            this.handlePinchEnd()
        }
    }

    handleTouchEnd = (event) => {
        if (this.state.dragging) {
            this.handlePanEnd()
        }

        if (this.state.pinching) {
            this.handlePinchEnd()
        }
    }

    handleTouchMove = (event) => {
        if (event.touches.length === 1 && this.state.dragging) {
            this.handlePan(event)
        }

        if (event.touches.length === 2 && this.state.pinching) {
            this.handlePinch(event)
        }
    }

    handlePinchStart = (event) => {
        event.preventDefault()
        disableZoom()
        this.state.pinching = true
        this.element.classList.add(STATES.PINCHING)

        const vector = {
            x: event.touches[0].clientX - event.touches[1].clientX,
            y: event.touches[0].clientY - event.touches[1].clientY,
        }

        const distance = Math.hypot(vector.x, vector.y)

        this.state.pinchFactorStart = this.matrix.scale
        this.state.pinchDistanceStart = distance

        this.log(`pinch start ${distance}`)
    }

    handlePinch = (event) => {
        if (!this.state.pinching) {
            return
        }

        const vector = {
            x: event.touches[0].clientX - event.touches[1].clientX,
            y: event.touches[0].clientY - event.touches[1].clientY,
        }

        const distance = Math.hypot(vector.x, vector.y)

        this.log(`pinch change ${distance}`)
        event.preventDefault()

        this.setZoom((this.state.pinchFactorStart * distance) / this.state.pinchDistanceStart)
    }

    handlePinchEnd = (event) => {
        if (!this.state.pinching) {
            return
        }

        enableZoom()
        this.state.pinching = false
        this.element.classList.remove(STATES.PINCHING)

        this.log('pinch end')
    }

    handlePanStart = (event) => {
        this.log('pan start')
        this.state.dragging = true
        this.element.classList.add(STATES.DRAGGING)
        if (event.touches) {
            this.state.clientXStart = event.touches[0].clientX
            this.state.clientYStart = event.touches[0].clientY
        } else {
            this.state.clientXStart = event.clientX
            this.state.clientYStart = event.clientY
        }

        this.matrix.startX = this.matrix.x
        this.matrix.startY = this.matrix.y
        this.emit('canvas:drag:start')
    }

    handlePanEnd = () => {
        if (!this.state.dragging) {
            return
        }

        this.log('pan end')

        this.state.dragging = false
        this.element.classList.remove(STATES.DRAGGING)

        const containerBox = this.ref.container.getBoundingClientRect()
        const svgClientBox = this.ref.svg.getBoundingClientRect()
        const svgBox = {
            left: svgClientBox.left - containerBox.left,
            top: svgClientBox.top - containerBox.top,
            width: svgClientBox.width,
            height: svgClientBox.height,
            paddingX: SVG_PADDING_X * this.matrix.scale,
            paddingY: SVG_PADDING_Y * this.matrix.scale,
        }

        let outOfWindow = false
        let x = this.matrix.x * this.matrix.scale
        let y = this.matrix.y * this.matrix.scale

        const box = {
            left: svgBox.left + svgBox.paddingX,
            right: svgBox.left + svgBox.width - svgBox.paddingX,
            top: svgBox.top + svgBox.paddingY,
            bottom: svgBox.top + svgBox.height - svgBox.paddingY,
        }

        const frame = {
            left: containerBox.width * LIMIT_BORDER_RATIO,
            right: containerBox.width * (1 - LIMIT_BORDER_RATIO),
            top: containerBox.height * LIMIT_BORDER_RATIO,
            bottom: containerBox.height * (1 - LIMIT_BORDER_RATIO),
        }

        if (box.left > frame.right) {
            x -= box.left - frame.right
            outOfWindow = true
        }

        if (box.right < frame.left) {
            x += frame.left - box.right
            outOfWindow = true
        }

        if (box.top > frame.bottom) {
            y -= box.top - frame.bottom
            outOfWindow = true
        }

        if (box.bottom < frame.top) {
            y += frame.top - box.bottom
            outOfWindow = true
        }

        if (outOfWindow) {
            this.matrix.x = x / this.matrix.scale
            this.matrix.y = y / this.matrix.scale
            this.render()
            this.emit('canvas:drag:end')
        } else {
            this.emit('canvas:drag:end')
        }

        setTimeout(() => (this.state.clickActive = true), 100)
    }

    handlePan = (event) => {
        if (!this.state.dragging) {
            return
        }

        this.log('pan move')

        this.state.clickActive = false

        let clientX = event.clientX
        let clientY = event.clientY

        if (event.touches) {
            if (event.touches.length > 1) {
                return
            }
            event.preventDefault()
            clientX = event.touches[0].clientX
            clientY = event.touches[0].clientY
        }

        this.matrix.x = this.matrix.startX + (clientX - this.state.clientXStart) / this.matrix.scale
        this.matrix.y = this.matrix.startY + (clientY - this.state.clientYStart) / this.matrix.scale

        this.render()
        this.emit('canvas:drag:change')
    }

    handleStoreClick = (event) => {
        event.preventDefault()
        if (!this.state.clickActive) {
            return
        }

        const target = event.target.closest('[data-modal-href]')

        if (!target) {
            return
        }

        const url = target.dataset.modalHref

        this.emit('store:click', {
            url: url,
            element: target,
        })
    }

    handleStoreMouseEnter = (event) => {
        const { title = '', desc = '', category = '', id = null, logoSrc = '', color = '', openingSoon = '' } = event.currentTarget.dataset
        const polygon = event.currentTarget.querySelector('[data-ref="polygon"]')
        const link = event.currentTarget.getAttribute('xlink:href')

        if (this.currentStore) {
            this.clear()
        }

        this.emit('store:mouseenter', {
            element: event.currentTarget,
            id,
            title,
            desc,
            category,
            polygon,
            logoSrc,
            link,
            openingSoon,
            color: polygon ? polygon.getAttribute('stroke') : color || null,
        })
    }

    handleStoreMouseLeave = (event) => {
        const { title = '', desc = '', id = null } = event.currentTarget.dataset

        this.emit('store:mouseleave', {
            element: event.currentTarget,
            id,
            title,
            desc,
        })
    }

    log(text) {
        return

        if (!this.logElement) {
            this.logElement = document.createElement('div')
            this.logElement.style.bakgroundColor = 'white'
            this.logElement.style.border = '1px solid black'
            this.logElement.style.width = '200px'
            this.logElement.style.position = 'absolute'
            this.logElement.style.top = '20px'
            this.logElement.style.left = '20px'
            this.element.appendChild(this.logElement)
        }

        this.logElement.textContent = text
    }

    parse() {
        const logos = this.ref.logos.reduce((acc, element) => {
            acc[element.dataset.id] = {
                id: element.dataset.id,
                element: element,
                fetched: false,
            }

            return acc
        }, {})

        this.cache = this.ref.floors
            .map((floor) => ({
                id: `${floor.dataset.floor}`,
                building: floor.dataset.building || 'main',
                element: floor,
                stores: queryAll('[data-ref="store"]', floor).map((element) => {
                    const id = element.dataset.id
                    const logoId = element.dataset.logoId
                    const polygonEl = element.querySelector('[data-ref="polygon"]')
                    const logo = logoId in logos ? logos[logoId] : null

                    let polygon = null

                    if (polygonEl) {
                        const polygonBBox = polygonEl.getBBox()

                        polygon = {
                            element: polygonEl,
                            x: polygonBBox.x,
                            y: polygonBBox.y,
                            width: polygonBBox.width,
                            height: polygonBBox.height,
                        }
                    }

                    const [logoX, logoY, logoWidth, logoHeight] = ['logoAreaX', 'logoAreaY', 'logoAreaWidth', 'logoAreaHeight'].map((key) => parseFloat(element.dataset[key])).map((value) => (isNaN(value) ? 0 : value))

                    const logoArea = {
                        x: logoX,
                        y: logoY,
                        width: logoWidth,
                        height: logoHeight,
                    }

                    let titleElement = null

                    if (polygon) {
                        titleElement = element.querySelector('[data-ref="title"]')

                        const width = logoArea.width || polygon.width
                        const height = logoArea.height || polygon.height
                        const rotated = height > width

                        const x = polygon.x + logoArea.x + width / 2
                        const y = polygon.y + logoArea.y + height / 2

                        if (rotated) {
                            const titleContent = titleElement.querySelector('[data-ref="titleContent"]')
                            titleContent.setAttribute('transform', 'rotate(-90)')
                        }

                        const box = titleElement.getBBox()
                        let scale = 1

                        if (box.height * scale > height * AREA_LOGO_RATIO) {
                            scale *= (height * AREA_LOGO_RATIO) / (box.height * scale)
                        }

                        if (box.width * scale > width * AREA_LOGO_RATIO) {
                            scale *= (width * AREA_LOGO_RATIO) / (box.width * scale)
                        }

                        titleElement.setAttribute('transform', `translate(${x}, ${y}) scale(${scale})`)
                    }

                    return {
                        id,
                        element,
                        polygon,
                        logoArea,
                        building: floor.dataset.building || 'main',
                        logo: polygon ? logo : null,
                        floor: floor.dataset.floor,
                        category: element.dataset.categoryId,
                        title: element.dataset.title,
                        titleElement,
                        description: (element.dataset.descripption || '').trim(),
                        keywords: (element.dataset.keywords || '').trim(),
                        openingSoon: (element.dataset.openingSoon || '').trim(),
                    }
                }),
            }))
            .reduce((acc, floor) => {
                acc[floor.id] = floor
                return acc
            }, {})
    }

    center() {
        this.matrix.x = -(this.viewBox.width - this.matrix.width) / 2
        this.matrix.y = -(this.viewBox.height - this.matrix.height) / 2

        this.setZoom(ZOOM_DEFAULT)

        if (this.ref.buildings.length) {
            this.focusBuilding(this.state.building)
        } else {
            this.render()
        }
    }

    render() {
        this.ref.scroll.style.transform = `translate(${Math.round(this.matrix.x * this.matrix.scale)}px,${Math.round(this.matrix.y * this.matrix.scale)}px) scale(${this.matrix.scale})`
    }

    renderStores() {
        if (this.currentFloor === null) {
            return
        }

        if (!this.cache[this.currentFloor]?.stores?.length) {
            return
        }

        this.cache[this.currentFloor].stores.forEach((store) => {
            if (store.polygon && store?.logo?.fetched) {
                this.renderStore(store)
            } else if (store.polygon && store.logo) {
                this.loadLogo(store)
            }
        })
    }

    renderStore(store) {
        if (this.currentFloor !== store.floor) {
            return
        }

        if (store.polygon && store.logo) {
            const area = {
                width: store.polygon.width * this.matrix.scale,
                height: store.polygon.height * this.matrix.scale,
            }

            const { logo, titleElement } = store
            const width = Math.min(LOGO_BASE_WIDTH, area.width * AREA_LOGO_RATIO)
            toggleClass(logo.element, 'is-active', logo.alwaysVisible || width > LOGO_VISIBILITY_THRESHOLD)
            toggleClass(titleElement, 'is-hidden', logo.alwaysVisible || width > LOGO_VISIBILITY_THRESHOLD)
        }
    }

    loadLogo(store) {
        const { logo, polygon, logoArea } = store

        const href = logo.element.getAttribute('data-href')

        const image = new Image()
        image.onload = () => {
            logo.element.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href)
            let ratio = 1
            if (!image.height || !image.width) {
                let buffer = document.createElement('div')
                buffer.style.position = 'fixed'
                buffer.style.top = '-1000px'
                buffer.style.left = '-1000px'
                image.width = 200
                buffer.appendChild(image)
                document.body.appendChild(buffer)
                const box = image.getBoundingClientRect()
                ratio = box.height / box.width

                document.body.removeChild(buffer)
                buffer = null
            } else {
                ratio = image.height / image.width
            }
            let area = {
                x: polygon.x,
                y: polygon.y,
                width: polygon.width,
                height: polygon.height,
            }

            if (logoArea.width && logoArea.height) {
                area = {
                    x: polygon.x + logoArea.x,
                    y: polygon.y + logoArea.y,
                    width: logoArea.width,
                    height: logoArea.height,
                }
            }

            let width = Math.min(LOGO_BASE_WIDTH, area.width * AREA_LOGO_RATIO)
            let height = width * ratio

            if (height > area.height * AREA_LOGO_RATIO) {
                height = area.height * AREA_LOGO_RATIO
                width = height / ratio
            }

            const x = area.x + area.width / 2 - width / 2
            const y = area.y + area.height / 2 - height / 2
            // const x = center.x - width/2
            // const y = center.y - height/2

            logo.element.setAttribute('width', width)
            logo.element.setAttribute('height', Math.round(width * ratio))
            logo.element.setAttribute('x', x)
            logo.element.setAttribute('y', y)
            store.logo = {
                ...store.logo,
                fetched: true,
                ratio,
                width,
                height,
                x,
                y,
                alwaysVisible: logo.element.getAttribute('data-visible') === 'true',
            }

            this.renderStore(store)
        }

        image.src = href
    }

    zoomIn() {
        this.setZoom(this.matrix.scale * ZOOM_STEP_FACTOR)
    }

    zoomOut() {
        this.setZoom(this.matrix.scale / ZOOM_STEP_FACTOR)
    }

    setZoom(factor) {
        factor = Math.max(Math.min(factor, ZOOM_MAX), ZOOM_MIN)

        const center = {
            x: -this.matrix.x * this.matrix.scale + this.ref.container.offsetWidth / 2,
            y: -this.matrix.y * this.matrix.scale + this.ref.container.offsetHeight / 2,
        }

        const scaleFactor = factor / this.matrix.scale

        // console.group('coords')

        // console.log('scale factor', scaleFactor)

        // matrix obsahuje hodnoty pozice bez transformace
        // prepocet na hodnoty s transformaci
        let x = this.matrix.x * this.matrix.scale
        let y = this.matrix.y * this.matrix.scale

        // console.log('s transformaci', x, y, this.matrix.x, this.matrix.y, this.matrix.scale)

        // posun stredu
        x += (1 - scaleFactor) * center.x
        y += (1 - scaleFactor) * center.y

        // console.log('posun stredu', x, y)

        // prepocet zpet na hodnotu bez transformace s pouzitim nove hodnoty zvetseni
        x = x / factor
        y = y / factor

        // console.log('bez transformace', x,y)
        // console.log('scale', this.matrix.scale)

        // hotovo
        this.matrix.x = x
        this.matrix.y = y

        // console.groupEnd()

        this.matrix.scale = factor
        // this.matrix.scaleStep = step

        // this.ref.svg.style.width = `${this.matrix.width * this.matrix.scale}px`
        // this.ref.svg.style.height = `${this.matrix.height * this.matrix.scale}px`

        // this.ref.scroll.style.width = `${this.matrix.width * this.matrix.scale}px`
        // this.ref.scroll.style.height = `${this.matrix.height * this.matrix.scale}px`

        this.render()
        this.renderStores()

        this.emit('canvas:zoom:change')
    }

    setBuilding(building, focus = true) {
        if (building !== this.state.building) {
            this.state.building = building
            if (focus) {
                this.focusBuilding(building)
            }
        }
    }

    focusBuilding(building) {
        const element = this.ref.buildings.filter((element) => element.dataset.id === building)[0]

        if (!element) {
            return
        }

        const box = element.getBoundingClientRect()
        const containerBox = this.element.getBoundingClientRect()

        // move center to new position
        const diff = {
            x: box.left - containerBox.left,
            y: box.top - containerBox.top,
        }

        const containerCenter = {
            x: containerBox.width / 2,
            y: (containerBox.height - TOOLBAR_SIZE) / 2 + TOOLBAR_SIZE,
        }

        const boxCenter = {
            x: diff.x + box.width / 2,
            y: diff.y + box.height / 2,
        }

        const vector = {
            x: containerCenter.x - boxCenter.x,
            y: containerCenter.y - boxCenter.y,
        }

        this.matrix.x += vector.x / this.matrix.scale
        this.matrix.y += vector.y / this.matrix.scale

        // set proper zoom
        let factor = this.matrix.scale
        let zoomChanged = false

        const bbox = element.getBBox()

        if (bbox.width * this.matrix.scale > containerBox.width * BUILDING_CONTAINER_RATIO || bbox.height * this.matrix.scale > containerBox.height * BUILDING_CONTAINER_RATIO) {
            for (let i = ZOOM_STEPS; i > 0; i--) {
                factor = this.getZoomFactor(i)
                if (bbox.width * factor < containerBox.width && bbox.height * factor < containerBox.height) {
                    break
                }
            }

            this.setZoom(factor)
        } else {
            this.render()
        }
    }

    setFloor(floor, resetView = false) {
        this.currentFloor = `${floor}`
        Object.keys(this.cache)
            .filter((key) => this.cache[key].building === this.state.building)
            .map((key) => this.cache[key].element)
            .forEach((element) => {
                if (element.dataset.floor === `${floor}`) {
                    element.classList.add('is-visible')
                    element.getBoundingClientRect()
                    element.classList.add('is-active')
                } else {
                    element.classList.remove('is-active')
                }
            })

        if (resetView) {
            this.focusBuilding(this.state.building)
        }

        this.renderStores()
        this.emit('floor:change', floor)
    }

    getZoomFactor(step) {
        const logMin = Math.log(ZOOM_MIN)
        const logMax = Math.log(ZOOM_MAX)
        const logCurrent = logMin + ((logMax - logMin) * step) / (ZOOM_STEPS - 1)
        return Math.exp(logCurrent)
    }

    getStores() {
        const stores = Object.keys(this.cache)
            .map((floorId) => this.cache[floorId])
            .map((floor) => floor.stores)
            .reduce((acc, stores) => acc.concat(stores), [])
            .map((store) => ({
                floor: store.floor,
                id: store.id,
                title: store.element.dataset.title || '',
                desc: store.element.dataset.desc || '',
                logoUrl: store?.logo?.element?.dataset?.href,
                description: store.description,
                element: store.element,
                keywords: store.keywords,
            }))

        return stores
    }

    showStore(id) {
        if (this.currentStore) {
            this.clear()
        }

        id = `${id}`

        const store = Object.keys(this.cache)
            .map((floorId) => this.cache[floorId])
            .map((floor) => floor.stores.filter((store) => store.id === id))
            .reduce((acc, stores) => acc.concat(stores), [])[0]

        if (store) {
            // move current store to map center
            const storeBox = store.polygon.element.getBoundingClientRect()
            const box = this.element.getBoundingClientRect()

            const vector = {
                x: box.width / 2 - (storeBox.left - box.left + storeBox.width / 2),
                y: box.height / 2 - (storeBox.top - box.top + storeBox.height / 2),
            }

            this.matrix.x = this.matrix.x + vector.x / this.matrix.scale
            this.matrix.y = this.matrix.y + vector.y / this.matrix.scale

            // zoom in if necessary
            if (this.matrix.scaleStep != ZOOM_DEFAULT + 1) {
                this.setZoom(ZOOM_DEFAULT + 1)
            }

            this.render()

            this.setFloor(store.floor)
            store.element.classList.add(STATES.ACTIVE)
            this.currentStore = store.element

            const { title = '', desc = '', category = '', id = null, logoSrc = '' } = store.element.dataset
            const polygon = store.element.querySelector('[data-ref="polygon"]')
            const link = store.element.getAttribute('xlink:href')

            this.emit('store:mouseenter', {
                element: store.element,
                id,
                title,
                desc,
                category,
                polygon,
                logoSrc,
                link,
                color: polygon ? polygon.getAttribute('stroke') : null,
            })
        }
    }

    filter(config = {}) {
        const keys = Object.keys(config)

        const stores = Object.keys(this.cache).flatMap((key) => this.cache[key].stores)

        if (!keys.length) {
            this.element.classList.remove(STATES.HIGHLIGHTED)
            stores.forEach((store) => store.element.classList.remove(STATES.FILTERED))
            this.emit('filter:reset')
            return
        }

        this.element.classList.add(STATES.HIGHLIGHTED)

        stores.forEach((store) => {
            const result = keys.filter((key) => store.element.dataset[key] === config[key])
            store.isFiltered = result.length === keys.length
        })

        stores.forEach((store) => toggleClass(store.element, STATES.FILTERED, store.isFiltered))

        // check current floor for available items
        const floorCounts = Object.keys(this.cache)
            .map((key) => {
                const count = this.cache[key].stores.filter((store) => store.isFiltered).length
                return {
                    key,
                    count,
                }
            })
            .reduce((acc, item) => {
                acc[item.key] = item.count
                return acc
            }, {})

        if (floorCounts[this.currentFloor] === 0) {
            const floor = Object.keys(floorCounts).filter((key) => key !== this.currentFloor && floorCounts[key] > 0)[0]
            if (floor != null) {
                this.setFloor(floor)
            }
        }

        this.emit('filter:change', {
            floors: floorCounts,
            config,
        })
    }

    clear() {
        if (this.currentStore) {
            this.currentStore.classList.remove(STATES.ACTIVE)
            this.currentStore = null
        }
    }
}
